import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Settings, MapPin, Loader2, Save, Sparkles } from 'lucide-react';

const CategoriesTab = ({
  categorySettings,
  setCategorySettings,
  neighborhoodSettings,
  setNeighborhoodSettings,
  saving,
  onSaveCategories,
  onSaveNeighborhoods,
  onAddCategory,
  onRemoveCategory,
  onAddNeighborhood,
  onRemoveNeighborhood,
  autofill,
  onStartAutofill
}) => {
  return (
    <div className="space-y-6">
      <Card className="rounded-2xl border-[#E0D6C8]">
        <CardHeader>
          <CardTitle className="text-xl font-['Manrope'] flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#5C4023]" />
            Category Settings
          </CardTitle>
          <CardDescription>
            Customize SDOH categories and filter behavior
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-sm font-medium">Filter Selection Mode</Label>
            <Select
              value={categorySettings.filter_mode}
              onValueChange={(v) => setCategorySettings({...categorySettings, filter_mode: v})}
            >
              <SelectTrigger data-testid="filter-mode-select" className="rounded-xl mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="multi">Multi-select (checkboxes)</SelectItem>
                <SelectItem value="single">Single-select (dropdown)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-[#888888] mt-1">How staff can filter by categories</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-medium">SDOH Categories</Label>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onAddCategory}
                data-testid="add-category-btn"
                className="rounded-full"
              >
                + Add Category
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {categorySettings.sdoh_categories.map((cat) => (
                <Badge 
                  key={cat}
                  className="bg-[#F3EFE9] text-[#5C4023] hover:bg-[#E0D6C8] cursor-pointer px-3 py-1.5 rounded-full"
                  onClick={() => onRemoveCategory(cat)}
                >
                  {cat}
                  <span className="ml-2 text-[#888888]">&times;</span>
                </Badge>
              ))}
            </div>
            <p className="text-xs text-[#888888] mt-2">Click a category to remove it</p>
          </div>

          <Separator />

          <Button
            onClick={onSaveCategories}
            disabled={saving}
            data-testid="save-categories-btn"
            className="w-full bg-[#5C4023] hover:bg-[#4A331C] rounded-full h-12"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Category Settings
          </Button>
        </CardContent>
      </Card>

      {/* Neighborhood Settings Card */}
      <Card className="rounded-2xl border-[#E0D6C8]">
        <CardHeader>
          <CardTitle className="text-xl font-['Manrope'] flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#5C4023]" />
            Neighborhood Settings
          </CardTitle>
          <CardDescription>
            Configure neighborhood options for filtering and resource locations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-medium">Neighborhoods</Label>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onAddNeighborhood}
                data-testid="add-neighborhood-btn"
                className="rounded-full"
              >
                + Add Neighborhood
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {neighborhoodSettings.neighborhoods?.map((neighborhood) => (
                <Badge 
                  key={neighborhood}
                  className="bg-[#E8DFD3] text-[#4A331C] hover:bg-[#DFD2BE] cursor-pointer px-3 py-1.5 rounded-full"
                  onClick={() => onRemoveNeighborhood(neighborhood)}
                >
                  {neighborhood}
                  <span className="ml-2 text-[#888888]">&times;</span>
                </Badge>
              ))}
            </div>
            <p className="text-xs text-[#888888] mt-2">Click a neighborhood to remove it. These appear in filters and when adding resources.</p>
          </div>

          <div className="rounded-xl border border-[#E0D6C8] bg-[#F3EFE9] p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#C87D3E]" />
                  Auto-fill Neighborhoods (AI)
                </p>
                <p className="text-xs text-[#888888] mt-1">
                  Infers each resource's Boston neighborhood from its address, zip code and organization. Only fills resources with no neighborhood set — review results afterward.
                </p>
              </div>
              <Button
                onClick={onStartAutofill}
                disabled={autofill?.status === 'processing'}
                data-testid="autofill-neighborhoods-btn"
                size="sm"
                className="rounded-full bg-[#C87D3E] hover:bg-[#B06A2F] text-white"
              >
                {autofill?.status === 'processing' ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" />{autofill.processed}/{autofill.total}</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />Run Auto-fill</>
                )}
              </Button>
            </div>
            {autofill?.status === 'complete' && (
              <p className="text-xs text-[#2B593F] mt-2" data-testid="autofill-result">
                ✓ {autofill.updated} of {autofill.total} resources updated
              </p>
            )}
          </div>

          <Separator />

          <Button
            onClick={onSaveNeighborhoods}
            disabled={saving}
            data-testid="save-neighborhoods-btn"
            className="w-full bg-[#5C4023] hover:bg-[#4A331C] rounded-full h-12"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Neighborhood Settings
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default CategoriesTab;
